import { Dropdown, Menu, Popconfirm, Modal, Tag, Button, Tooltip } from "antd";
import Table, { ColumnsType } from "antd/es/table";
import { QUERY_KEY_FOR_TEAM_MEMBERS, useGetMembers } from "../hooks/useGetMembers";
import { ITeamMembers, TUpdatePackagePlan } from "../types";
import { useUpdatePackagePlan } from "../hooks/useUpdatePackagePlan";
import { useRemoveTeamMember } from "../hooks/useDeleteMember";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { openNotification } from "utils/notification";
import { useQueryClient } from "react-query";
import { AttachUserPlan } from "./AttachUserPlan";
import { useState } from "react";
import { LockOutlined } from "@ant-design/icons";
import { TeamPermissionManager } from "./TeamPermissionManager";

export const MemberTable = () => {
  const { token, userInfo } = useGetUserInfo();
  const queryClient = useQueryClient();
  const { data, isLoading } = useGetMembers();
  const { mutate, isLoading: loading } = useUpdatePackagePlan();
  const { mutate: deleteMember, isLoading: deleting } = useRemoveTeamMember();
  const [attach, setAttach] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [permissionModalVisible, setPermissionModalVisible] = useState(false);
  const [selectedMember, setSelectedMember] = useState<ITeamMembers | null>(null);

  const isCorporate = userInfo?.recruiter?.is_corporate;

  const detachPlan = (values: TUpdatePackagePlan) => {
    mutate(
      {
        id: values.id,
        user_id: values.user_id,
        token: token,
        operation: "remove-package-plan"
      },
      {
        onError: (err: any) => {
          openNotification({
            title: "Error",
            state: "error",
            description: "Error occurred try again",
            duration: 4.5,
          });
        },
        onSuccess: (res: any) => {
          openNotification({
            title: "Success",
            state: "success",
            description: "",
            duration: 4.5,
          });
          queryClient.refetchQueries([QUERY_KEY_FOR_TEAM_MEMBERS]);
        },
      }
    );
  };

  const handleDeleteMember = (userId: string) => {
    deleteMember(userId,
      {
        onError: (err: any) => {
          openNotification({
            title: "Error",
            state: "error",
            description: "Error occurred while deleting the member. Please try again.",
            duration: 4.5,
          });
        },
        onSuccess: (res: any) => {
          openNotification({
            title: "Success",
            state: "success",
            description: "Member deleted successfully",
            duration: 4.5,
          });
          queryClient.invalidateQueries([QUERY_KEY_FOR_TEAM_MEMBERS]);
        },
      }
    );
  };

  const handleManagePermissions = (member: ITeamMembers) => {
    setSelectedMember(member);
    setPermissionModalVisible(true);
  };

  const columns: ColumnsType<ITeamMembers> = [
    {
      title: "Name",
      dataIndex: "name",
      render: (_, val) => <span>{val?.user?.name}</span>,
    },
    {
      title: "Email",
      dataIndex: "email",
      render: (_, val) => <span>{val?.user?.email}</span>,
    },
    {
      title: "Plan",
      dataIndex: "plan",
      render: (_, val) => (
        <span>
          {val?.user?.corporate_subscription ?
            val?.user?.corporate_subscription?.package.name :
            'none'}
        </span>
      ),
    },
    {
      title: "Permissions",
      dataIndex: "permissions",
      render: (_, val) => {

        const permissionCount = val.permissions?.length ||
          val.team_permissions?.length ||
          0;
        return (
          <Tag color={permissionCount > 0 ? "blue" : "default"}>
            {permissionCount} permissions
          </Tag>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "invite_status",
      render: (_, val) => (
        <div className={`inline-flex items-center justify-center gap-2 rounded-xl px-2 text-sm pb-1${val?.invite_status === "pending" ? "bg-yellow-100 text-yellow-600" : val?.invite_status === "active" ? "bg-green-100 text-green-700" : ''}`}>
          <i
            className={`ri-checkbox-blank-circle-fill  ${val?.invite_status === "active" ? "text-green-700" : val?.invite_status === "pending" ? "text-yellow-600" : ''}`}
            style={{ fontSize: 10 }}
          ></i>
          <span className="text-sm pb-1">{val?.invite_status}</span>
        </div>
      ),
    },
    {
      title: "Actions",
      dataIndex: "action",
      render: (_, val) => (
        <div className="flex items-center space-x-2">
          {/* Permission Management Button - Only for corporate users */}
          {isCorporate && (
            <Tooltip title="Manage Permissions">
              <Button
                type="text"
                icon={<LockOutlined />}
                onClick={() => handleManagePermissions(val)}
                size="small"
              />
            </Tooltip>
          )}

          <Dropdown
            trigger={["click"]}
            overlay={
              <Menu>
                {val.user.corporate_subscription === null &&
                  <Menu.Item key="2" onClick={() => {
                    setSelectedUser(val.user_id);
                    setAttach(true)
                  }}>
                    Asign Package Plan
                  </Menu.Item>
                }
                {val.user.corporate_subscription &&
                  <Menu.Item key="3">
                    <Popconfirm
                      okButtonProps={{
                        style: { backgroundColor: '#f5222d', color: '#ffffff' },
                      }}
                      title="Remove plan from user"
                      description="Are you sure to Remove plan from user?"
                      onConfirm={() => detachPlan({
                        'user_id': val?.user_id,
                        'id': val.user.corporate_subscription.id
                      })}
                    >
                      Remove Package Plan
                    </Popconfirm>
                  </Menu.Item>
                }
                <Menu.Item key="4">Disable</Menu.Item>
                <Menu.Item key="5">
                  <Popconfirm
                    title="Are you sure you want to delete this member?"
                    onConfirm={() => handleDeleteMember(val.user_id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    Delete Member
                  </Popconfirm>
                </Menu.Item>
              </Menu>
            }
          >
            <i className="ri-more-2-fill text-lg cursor-pointer"></i>
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <Table
        className="bg-white rounded-md shadow border mt-8"
        columns={columns}
        dataSource={data}
        scroll={{ x: 650 }}
        loading={isLoading || deleting}
      />

      <AttachUserPlan
        open={attach}
        handleClose={() => setAttach(false)}
        user_id={selectedUser}
      />

      {/* Permission Management Modal */}
      <Modal
        title={`Manage Permissions for ${selectedMember?.user?.name}`}
        open={permissionModalVisible}
        onCancel={() => {
          setPermissionModalVisible(false);
          setSelectedMember(null);
        }}
        width={800}
        footer={null}
        destroyOnClose
      >
        {selectedMember && (
          <TeamPermissionManager
            userId={selectedMember.user_id}
            onSuccess={() => {
              setPermissionModalVisible(false);
              queryClient.invalidateQueries([QUERY_KEY_FOR_TEAM_MEMBERS]);
              openNotification({
                title: "Success",
                state: "success",
                description: "Permissions updated successfully",
                duration: 4.5,
              });
            }}
          />
        )}
      </Modal>
    </>
  );
};